// Wire-shape primitives shared across endpoints.
//
// Two patterns to keep in mind:
//
//   1. Where a route's response shape is the engine entity verbatim, we
//      re-export the engine schema rather than re-declare it. The engine is
//      the source of truth; api-types just composes those schemas into HTTP
//      contracts.
//
//   2. Errors follow a single envelope so clients can branch on `code`. The
//      Fastify error handler in core-api emits exactly this shape from any
//      DomainError (see @bitacorapro/shared/errors).
import { EJE_FUENTE_CODES } from "@bitacorapro/core-engine";
import { z } from "zod";
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
// Pagination — keep cursor-style so we can add cursor support later without
// breaking callers; for now `limit` is honored and `cursor` is reserved.
export const paginationQuerySchema = z.object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().optional(),
});
export function paginated(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().optional(),
    });
}
/** Un par `{codigo, nombre}` del catálogo de taxonomía postventa,
 *  RESUELTO server-side contra la versión de catálogo de la fila que lo
 *  emite — nunca la vigente. El catálogo en sí (criterio, ayuda, el
 *  mapeo) jamás cruza el wire; sólo estas proyecciones ya resueltas
 *  (fitness: taxonomy-catalog-never-on-the-wire).
 *
 *  Vive acá, y no en `findings.ts` donde nació, porque hoy lo comparten
 *  dos módulos que se importan entre sí: `findings.ts` arma con él las
 *  pick-lists de `GET /findings/:id` (`taxonomyOptionsSchema`), y
 *  `assets.ts` lo usa para el `recinto` resuelto del feed de
 *  observaciones. Declararlo en cualquiera de los dos cerraría el ciclo
 *  `assets.ts ↔ findings.ts` (findings.ts ya hace
 *  `assetObservationSchema.omit(...)` en tope de módulo, así que el
 *  ciclo no sería benigno: reventaría en TDZ al cargar el paquete).
 *  Redeclarar el par a mano en cada lado era la otra salida — la deriva
 *  que este repo ya viene pagando en otros lados. */
export const taxonomyOptionSchema = z.object({
    codigo: z.string().min(1).max(8),
    nombre: z.string().min(1),
});
/** #0646 — quién responde por el valor que un eje recibe en ESTE request.
 *  Derivado del engine (`EJE_FUENTE_CODES`), jamás re-tipeado acá.
 *
 *  Vive acá y no en `findings.ts`, donde nació, por la MISMA trampa que
 *  documenta `taxonomyOptionSchema` arriba: F5 (postventa, carril del
 *  propietario) lo necesita en `assets.ts` para el mapa angosto
 *  `ownerEjeFuenteSchema`, y `findings.ts` ya hace
 *  `assetObservationSchema.omit(...)` en tope de módulo — declararlo en
 *  `assets.ts` cerraría el ciclo `assets.ts ↔ findings.ts` y reventaría
 *  en TDZ al CARGAR el paquete, no al compilarlo. Lo que se comparte es
 *  el VOCABULARIO; el mapa por eje no: `ejeFuenteMapSchema` se queda en
 *  `findings.ts` porque sus llaves derivan de `CLASSIFICATION_AXIS_CODES`,
 *  el set del gate de ADR-0121, y ensancharlo cambiaría la retención de
 *  toda fila clasificada de todas las verticales. */
export const ejeFuenteSchema = z.enum(EJE_FUENTE_CODES);
/** The pick-lists the PWA's remedy screens render (elemento, tipo de
 *  falla, and recinto if a picker ships), RESOLVED at the row's catalog
 *  version: `codigo` + `nombre` only, plus `campoAdicional` on tipos de
 *  falla so the conditional sub-question (¿hay agua? / ¿cuánto mide?)
 *  is data the wire declares, not a client-hardcoded "TF-04". The
 *  catalog itself — criterio, ayuda, the mapping — never crosses; same
 *  discipline as `candidateCardSchema`. Bundled on GET /findings/:id
 *  rather than served by a second route so the options can never
 *  disagree with the row's `taxonomyVersion` across two requests.
 *  El par `{codigo, nombre}` que las compone es `taxonomyOptionSchema`,
 *  acá arriba.
 *
 *  Vive acá, y no en `findings.ts` donde nació, por la misma trampa que
 *  documenta `taxonomyOptionSchema`: F5 lo necesita en `assets.ts` para
 *  las `opciones` de `POST /assets/:id/observations/classify`, y
 *  `findings.ts` ya importa `assetObservationSchema` de `assets.ts` y lo
 *  usa en tope de módulo. Declararlo en `assets.ts` cerraría el ciclo y
 *  el fallo no sería de compilación sino de CARGA (TDZ). Los consumidores
 *  no cambian: `index.ts` ya hace `export * from "./common.js"`, así que
 *  el nombre sigue saliendo de la raíz del paquete. */
export const taxonomyOptionsSchema = z.object({
    taxonomyVersion: z.string(),
    recintos: z.array(taxonomyOptionSchema),
    elementos: z.array(taxonomyOptionSchema),
    tiposFalla: z.array(taxonomyOptionSchema.extend({
        campoAdicional: z.enum(["agua_activa", "ancho_mm"]).nullable(),
    })),
});
// ── Read model de la observación (S2, ADR-0105 / ADR-0129) ──────────
// Piezas compartidas por el feed del panel (`postventa-dashboard.ts`) y
// el detalle (`findings.ts`). Viven acá por la misma razón que
// `taxonomyOptionSchema`: los dos módulos ya se importan entre sí y
// redeclarar el par en cada lado es la deriva que este repo evita.
/** De dónde sale la S-code que la fila MUESTRA: `derivada` de los
 *  booleanos P1/P2/P3 (`derived_severity`), `legado` del
 *  `severities[].legacyDisplayAs` de la receta para una fila anterior a
 *  la taxonomía. Espejo del `origin` de `effectiveObservationSeverity`
 *  del engine — sólo lectura, jamás un escritor (fitness:
 *  effective-severity-never-persisted). */
export const severityOriginSchema = z.enum(["derivada", "legado"]);
/** Una foto con su leyenda, para las superficies autenticadas. El feed
 *  conserva `evidenceUrls` (string[]) un ciclo para no romper clientes
 *  desplegados; los consumidores nuevos leen esto. */
export const observationEvidenceItemSchema = z.object({
    url: z.string().url(),
    caption: z.string().nullable(),
});
/** Lo que quien reporta dijo, en sus palabras. `dictada` es la
 *  transcripción del audio; `kind: null` es el texto crudo
 *  (`findings.original_text`), que puede haber sido REDACTADO por el
 *  endpoint regulatorio (Ley 21.719 art. 7): entonces `text` es null y
 *  `redacted` true, y NINGÚN servidor rellena ese hueco con
 *  `humanDescription` / `aiDescription` — sería devolver por otra
 *  puerta lo que la redacción quitó. */
export const ownerDescriptionSchema = z.object({
    kind: z.enum(["dictada", "escrita"]).nullable(),
    text: z.string().nullable(),
    redacted: z.boolean(),
});
/** El carril desde el que se escribió un hito del hilo, para que el
 *  cliente pinte «propietario / inmobiliaria / contratista / sistema»
 *  sin adivinarlo por `eventType`. Las cuatro primeras son las de
 *  `ObservationStatusActor` del engine (recipe.ts — schema no
 *  exportado, de ahí el literal); `system` es el hito sin actor. */
export const timelineActorLaneSchema = z.enum([
    "asset_party",
    "assigning_party",
    "share_link",
    "session_user",
    "system",
]);
//# sourceMappingURL=common.js.map